import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

/* ── Hero ── */
export const StyledHero = styled.section`
  background: url(/assets/hero-bg.webp);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center 0%;
  color: ${CSSVars.white};
  padding: 140px 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  h1 {
    font-size: 1.875rem;
    font-weight: 300;
    margin-bottom: 20px;
    line-height: 1.15;
    position: relative;
    background: linear-gradient(277deg, #fff 48.61%, #808aad 109.97%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    .highlight {
      font-weight: 600;
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: ${CSSVars.fontSizeMD};
    color: rgba(255, 255, 255, 0.8);
    max-width: 620px;
    margin: 0 auto;
    line-height: 1.7;
    font-weight: 300;
    position: relative;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSizeLG};
    }
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 160px 40px 160px;
    h1 { font-size: ${CSSVars.fontSize2XL}; }
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 180px 40px 180px;
    h1 { font-size: ${CSSVars.fontSize3XL}; }
  }
`;

export const StyledStatBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 28px;
  margin-top: 28px;
  position: relative;

  span {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    white-space: nowrap;
  }
`;

export const StyledHeroCta = styled.a`
  display: inline-block;
  margin-top: 32px;
  padding: 14px 32px;
  background: ${CSSVars.blue500};
  color: ${CSSVars.white};
  border-radius: 8px;
  font-weight: 700;
  font-size: ${CSSVars.fontSizeMD};
  text-decoration: none;
  transition: background 0.3s;
  position: relative;

  &:hover {
    background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
  }
`;

/* ── Sections shared ── */
export const StyledSection = styled.section<{ $bg?: string }>`
  padding: 60px 20px;
  background: ${({ $bg }) => $bg || CSSVars.white};
  border-top: 1px solid rgba(0, 0, 0, 0.07);

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 80px 30px;
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 110px 40px;
  }
`;

export const StyledInner = styled.div<{ $maxWidth?: string }>`
  max-width: ${({ $maxWidth }) => $maxWidth || '1000px'};
  margin: 0 auto;
`;

export const StyledSectionLabel = styled.p`
  font-size: ${CSSVars.fontSizeXS};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${CSSVars.blue500};
  margin-bottom: 14px;
`;

export const StyledSectionHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${CSSVars.black};
  margin-bottom: 18px;
  line-height: 1.25;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    font-size: ${CSSVars.fontSize2XL};
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    font-size: ${CSSVars.fontSize3XL};
  }
`;

export const StyledSectionIntro = styled.p`
  font-size: ${CSSVars.fontSizeMD};
  color: ${CSSVars.gray600};
  line-height: 1.75;
  max-width: 680px;
  font-weight: 300;
`;

/* ── Widget cards grid ── */
export const StyledWidgetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 40px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledWidgetCard = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 22px 18px;
  @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 28px 24px; }
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
  text-align: center;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  img {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    object-fit: contain;
  }

  .widget-icon-placeholder {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: rgba(76, 79, 255, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${CSSVars.blue500};
    font-size: 24px;
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    font-weight: 300;
    flex: 1;
  }

  a {
    padding: 9px 18px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    border-radius: 8px;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    text-decoration: none;
    transition: background 0.3s;
    margin-top: auto;

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }
`;

/* ── Section CTA link ── */
export const StyledSectionCta = styled.a<{ $center?: boolean }>`
  display: block;
  width: fit-content;
  margin: 32px ${({ $center }) => ($center ? 'auto' : '0')} 0;
  padding: 14px 48px;
  background: ${CSSVars.blue500};
  color: ${CSSVars.white};
  border-radius: 8px;
  font-size: ${CSSVars.fontSizeSM};
  font-weight: 700;
  text-decoration: none;
  transition: background 0.3s;

  &:hover {
    background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
  }
`;

/* ── Benefits ── */
export const StyledBenefitsList = styled.div`
  margin-top: 32px;
  max-width: 700px;
`;

export const StyledBenefitItem = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 18px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);

  &:last-child { border-bottom: none; }

  .check {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${CSSVars.blue50};
    color: ${CSSVars.blue500};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
  }

  p {
    font-size: ${CSSVars.fontSizeMD};
    color: ${CSSVars.gray700};
    line-height: 1.65;
    font-weight: 300;
    margin: 0;
  }
`;

/* ── Platform carousel ── */
export const StyledPlatformCarousel = styled.div`
  overflow: hidden;
  margin-top: 36px;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40px;
    z-index: 1;
    pointer-events: none;

    @media screen and (min-width: ${CSSVars.breakpointMD}) { width: 80px; }
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, ${CSSVars.white} 0%, transparent 100%);
  }

  &::after {
    right: 0;
    background: linear-gradient(270deg, ${CSSVars.white} 0%, transparent 100%);
  }

  .carousel-track {
    display: flex;
    align-items: center;
    gap: 32px;
    @media screen and (min-width: ${CSSVars.breakpointMD}) { gap: 48px; }
    width: max-content;
    animation: scrollLogos 30s linear infinite;
  }

  .logo-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    opacity: 0.55;
    transition: opacity 0.3s;

    &:hover {
      opacity: 1;
    }

    img {
      height: 36px;
      width: auto;
      object-fit: contain;
    }
  }

  @keyframes scrollLogos {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

/* ── CTA Banner ── */
export const StyledCtaBanner = styled.div`
  background: linear-gradient(135deg, ${CSSVars.blue500}, #6b8cff);
  border-radius: 24px;
  padding: 48px 28px;
  text-align: center;
  margin: 60px auto 0;
  max-width: 900px;

  h2 {
    color: ${CSSVars.white};
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 12px;
    line-height: 1.3;
  }

  p {
    color: rgba(255, 255, 255, 0.85);
    font-size: ${CSSVars.fontSizeMD};
    margin-bottom: 24px;
    font-weight: 300;
    line-height: 1.6;
  }

  a {
    display: inline-block;
    background: ${CSSVars.white};
    color: ${CSSVars.blue500};
    font-weight: 700;
    padding: 12px 28px;
    border-radius: 8px;
    text-decoration: none;
    font-size: ${CSSVars.fontSizeMD};
    transition: transform 0.2s;

    &:hover { transform: translateY(-2px); }
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 64px 48px;
    h2 { font-size: ${CSSVars.fontSize2XL}; }
  }
`;
